import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const EMPLOYEE_PARAM = 'employeeId';

/**
 * Whose portal is on screen, kept in `?employeeId=` -- `undefined` for the reader's own, which is
 * what the portal opens on and what every employee without a team ever sees.
 *
 * The address is the single place that holds it, the way `?tab=` and `?period=` already do: the
 * picker beside the tabs, the reads of each tab and the banner above them all read it from there,
 * so none of them has to be handed it and none of them can disagree with another. A reload or a
 * shared link therefore reopens the same portal, and the server refuses an employee the reader's
 * performance scope does not reach whatever the address says.
 *
 * Replaced rather than pushed, like the open tab: the back button leaves the portal instead of
 * walking back through every employee looked at. The other parameters are kept.
 */
export const useViewedEmployee = () => {
    const history = useHistory();
    const location = useLocation();

    const viewedEmployeeId = new URLSearchParams(location.search).get(EMPLOYEE_PARAM) ?? undefined;

    const viewPortalOf = useCallback(
        (employeeId: string | undefined) => {
            const params = new URLSearchParams(location.search);
            if (employeeId) {
                params.set(EMPLOYEE_PARAM, employeeId);
            } else {
                params.delete(EMPLOYEE_PARAM);
            }
            history.replace({ search: params.toString() });
        },
        [history, location.search],
    );

    return { viewedEmployeeId, viewPortalOf };
};
